// Pure helpers for organizing an issue list by page (team review + extension).
// Shared so both surfaces use one implementation instead of drifting copies.
// UI-render and "needs details" rules stay per-surface (extension uses pinNeedsDetails;
// review-app uses issueNeedsDetails); these are the page-grouping/triage primitives only.
/** Group key = origin + pathname + search (hash excluded). Falls back to raw string. */
export function pageGroupKey(url) {
    if (!url)
        return '';
    try {
        const u = new URL(url);
        return u.origin + u.pathname + u.search;
    }
    catch {
        return url;
    }
}
/**
 * Human label for a page group. The raw pathname is low-meaning to a reviewer who
 * didn't capture the issue, so:
 *  - the root path renders as "Home" (not a bare "/")
 *  - long paths (UUID routes, hashes) are middle-truncated, keeping the route stem
 *    AND a recognizable tail visible instead of an end-ellipsis chopping the tail off.
 */
export function prettyPageLabel(url, maxLen = 42) {
    if (!url)
        return '(unknown page)';
    let raw;
    try {
        const u = new URL(url);
        raw = u.pathname + u.search;
    }
    catch {
        raw = url;
    }
    if (raw === '' || raw === '/')
        return 'Home';
    if (raw.length <= maxLen)
        return raw;
    const tail = raw.slice(-8);
    const head = raw.slice(0, maxLen - tail.length - 1); // -1 for the ellipsis
    return head + '…' + tail;
}
/** Host (with port) of a page URL, or '' when absent/unparseable. */
export function pageGroupHost(url) {
    if (!url)
        return '';
    try {
        return new URL(url).host;
    }
    catch {
        return '';
    }
}
/**
 * Labels for an ORDERED list of page groups (one representative URL per group).
 * Keeps prettyPageLabel's clean single-site output, but disambiguates labels that
 * collide across DIFFERENT hosts by appending the host. Without this, the root of
 * every site renders as "Home", so a project holding issues captured on two sites
 * showed two indistinguishable "Home" headers and a reviewer couldn't tell which
 * site a group belonged to. Only the labels that actually collide get a host
 * suffix, so the common single-site review is unchanged.
 */
export function labelPageGroups(urls) {
    const base = urls.map((u) => prettyPageLabel(u));
    const counts = new Map();
    for (const label of base)
        counts.set(label, (counts.get(label) ?? 0) + 1);
    return base.map((label, i) => {
        if ((counts.get(label) ?? 0) <= 1)
            return label;
        const host = pageGroupHost(urls[i]);
        return host ? `${label} · ${host}` : label;
    });
}
/** Float the current page group to the top, keeping the rest in their original order. */
export function orderPageGroups(order, currentKey) {
    if (!currentKey || !order.includes(currentKey))
        return order;
    return [currentKey, ...order.filter((k) => k !== currentKey)];
}
/** Lower rank = more severe. Unknown severities sort as medium (matches the visual fallback). */
export const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const severityRank = (sev) => SEVERITY_RANK[sev] ?? SEVERITY_RANK.medium;
/** Stable sort, most severe first. Generic so the caller's concrete type survives. Does not mutate. */
export function sortIssuesBySeverity(issues) {
    // Ignored rows sort LAST within the group (Emily, 2026-09-22): severity
    // order still holds inside each half, so an ignored critical never outranks
    // a live low. A null ignoredAt is a live row (Un-ignore nulls the column).
    return issues
        .map((issue, index) => ({ issue, index, ignored: issue.ignoredAt ? 1 : 0 }))
        .sort((a, b) => a.ignored - b.ignored || severityRank(a.issue.severity) - severityRank(b.issue.severity) || a.index - b.index)
        .map((x) => x.issue);
}
/** Parse a createdAt into epoch ms; NaN for missing/unparseable (so it never wins as "newest"). */
function createdAtMs(createdAt) {
    if (createdAt == null)
        return NaN;
    const ms = createdAt instanceof Date ? createdAt.getTime() : new Date(createdAt).getTime();
    return ms;
}
/**
 * The id of the most-recently-created issue in the list, or null when none carry a
 * usable createdAt. Used to float a fresh capture to the top of its page group so a
 * just-created issue is immediately visible (matches the "newest on top" convention
 * of GitHub Issues et al.) without giving up severity order for the rest.
 */
export function newestIssueId(issues) {
    let bestId = null;
    let bestMs = -Infinity;
    for (const issue of issues) {
        if (issue.id == null)
            continue;
        const ms = createdAtMs(issue.createdAt);
        if (Number.isNaN(ms))
            continue;
        if (ms > bestMs) {
            bestMs = ms;
            bestId = issue.id;
        }
    }
    return bestId;
}
/**
 * Severity sort, but with the just-created issue (`newestId`) floated to the very top
 * of the group. Everything below stays in severity order. Falls back to a plain
 * severity sort when `newestId` is null or not in this group. Does not mutate.
 */
export function sortIssuesBySeverityFloatingNewest(issues, newestId) {
    if (!newestId)
        return sortIssuesBySeverity(issues);
    const newest = issues.find((i) => i.id === newestId);
    // An ignored row never floats: it belongs at the bottom whatever its age.
    if (!newest || newest.ignoredAt)
        return sortIssuesBySeverity(issues);
    const rest = issues.filter((i) => i.id !== newestId);
    return [newest, ...sortIssuesBySeverity(rest)];
}
/** The most severe level present in a group, or null when empty. */
export function groupTopSeverity(issues) {
    if (issues.length === 0)
        return null;
    const top = issues.reduce((best, i) => Math.min(best, severityRank(i.severity)), Number.POSITIVE_INFINITY);
    const entry = Object.entries(SEVERITY_RANK).find(([, rank]) => rank === top);
    return entry?.[0] ?? 'medium';
}
/**
 * The order a grouped issue list shows its rows (2026-10-20): the current page
 * first, then pages in first-seen order; inside a page, severity order with the
 * newest issue floated first and ignored rows last. The same primitives the
 * extension and team review tables render with, so Copy puts issues on the
 * clipboard in the order the reader sees them. Pass the WHOLE list and filter
 * afterwards: "newest" is the newest of what you pass. Returns a new array.
 */
export function orderIssuesAsListed(issues, currentUrl) {
    const newestId = newestIssueId(issues);
    const order = [];
    const groups = new Map();
    for (const issue of issues) {
        const key = pageGroupKey(issue.pageUrl ?? undefined);
        if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
        }
        groups.get(key).push(issue);
    }
    return orderPageGroups(order, pageGroupKey(currentUrl))
        .flatMap((key) => sortIssuesBySeverityFloatingNewest(groups.get(key), newestId));
}
/**
 * The Issues count a surface shows: rows that are not ignored. Ignore is a
 * FLAG that keeps the row in the list (dimmed, sorted last, 2026-09-22), and
 * the server's own card figures already leave ignored rows out
 * (project-query.service.ts:107); a count of the list stood still when a row
 * was ignored (Emily, 2026-10-27). A missing or null ignoredAt is a live row.
 */
export function activeIssueCount(issues) {
    return issues.reduce((n, issue) => n + (issue.ignoredAt ? 0 : 1), 0);
}
/**
 * Rows an Issues count still owes beyond the page in hand: the server's active
 * total (its `total` less its `ignoredTotal`) minus the active rows loaded,
 * never negative. Ignored rows sort last on the server, so the loaded page
 * holds every active row before any ignored one, and the remainder stays
 * honest as the loaded array moves (a delete, a create, an ignore) with no
 * refetch in between.
 */
export function unloadedActiveIssueCount(total, ignoredTotal, loaded) {
    return Math.max(0, total - ignoredTotal - activeIssueCount(loaded));
}
//# sourceMappingURL=issueGrouping.js.map